import { Scores } from "model/Scores";
import {
  CupModePoints,
  getPointsDistribution,
} from "model/Match/Set/CupMode/Format/CupModePoints";
import { CupModeRound } from "model/Match/Set/CupMode/Track/CupModeRound";
import { CupModeRoundResult } from "model/Match/Set/CupMode/Track/CupModeRoundResult";
import { getPoints } from "model/Match/Set/PointsDistribution";
import { TrackRound } from "model/Match/Track/TrackRound";
import {
  Roster,
  getRosterId,
  getRosterDriverIds,
  getNumPlayingRosters,
} from "model/Roster/Roster";
import {
  getFcmWinnerPoints,
  PlayTill,
} from "model/Match/Set/CupMode/Format/CupModeFormat";
import { RosterScope } from "model/Roster/RosterScope";

export const mapForwardCupModeRounds = (
  baseRounds: TrackRound[],
  rosters: Roster[],
  rosterScope: RosterScope,
  runningScores: Scores,
  points: CupModePoints,
  finalist: boolean,
  pointsLimit: number,
  driversPerTeam: number,
  requiredTeamWinners: number | undefined,
  playTill: PlayTill,
  completedDriverIds: string[],
  completedRosterIds: string[]
) => {
  return baseRounds.map((r) =>
    mapForwardCupModeRound(
      r,
      rosters,
      rosterScope,
      runningScores,
      points,
      finalist,
      pointsLimit,
      driversPerTeam,
      requiredTeamWinners,
      playTill,
      completedDriverIds,
      completedRosterIds
    )
  );
};

const mapForwardCupModeRound = (
  base: TrackRound,
  rosters: Roster[],
  rosterScope: RosterScope,
  runningScores: Scores,
  points: CupModePoints,
  finalist: boolean,
  pointsLimit: number,
  driversPerTeam: number,
  requiredTeamWinners: number | undefined,
  playTill: PlayTill,
  completedDriverIds: string[],
  completedRosterIds: string[]
): CupModeRound => {
  if (!finalist) {
    return {
      ...base,
      results: [],
    };
  }

  const numDriversAtStart =
    getNumPlayingRosters(rosters, false) * driversPerTeam;
  const numDriversLeft = numDriversAtStart - completedDriverIds.length;
  const pointsDist = getPointsDistribution(points, numDriversLeft);
  if (numDriversLeft <= 1 || !pointsDist) {
    return {
      ...base,
      results: [],
    };
  }

  // If more finish than expected, limit to 1 to avoid removing score or drawing with finalist
  const winnerPoints = getFcmWinnerPoints(
    playTill,
    rosters.length,
    completedRosterIds.length
  );

  const results: CupModeRoundResult[] = [];
  for (let i = 0; i < rosters.length; i++) {
    const roster = rosters[i];
    const rosterId = getRosterId(roster);
    if (!rosterId || completedRosterIds.includes(rosterId)) {
      continue;
    }

    const rosterDriverIds = getRosterDriverIds(roster, rosterScope) ?? [];
    const rosterTimes = base.times.filter((t) =>
      rosterDriverIds.includes(t.driverId)
    );

    if (rosterTimes.length === 0) {
      continue;
    }

    const rosterPointsAtStart = runningScores[rosterId] ?? 0;
    const rosterPointsDelta = rosterTimes.reduce(
      (a, b) => a + getPoints(pointsDist, b),
      0
    );

    if (rosterPointsAtStart >= pointsLimit) {
      const rosterFirstPlace = rosterTimes.find((t) => t.position === 1);
      if (!!rosterFirstPlace) {
        if (!completedDriverIds.includes(rosterFirstPlace.driverId)) {
          completedDriverIds.push(rosterFirstPlace.driverId);
        }
      }
    }

    var rosterPointsAtEnd: number;
    const rosterCompletedDriverIds = completedDriverIds.filter((id) =>
      rosterDriverIds.includes(id)
    );

    if (
      rosterCompletedDriverIds.length >= (requiredTeamWinners ?? driversPerTeam)
    ) {
      completedRosterIds.push(rosterId);
      rosterTimes
        .flatMap((t) => t.driverId)
        .forEach((id) => {
          if (!completedDriverIds.includes(id)) {
            completedDriverIds.push(id);
          }
        });

      rosterPointsAtEnd = pointsLimit + winnerPoints;
    } else {
      rosterPointsAtEnd = Math.min(
        rosterPointsAtStart + rosterPointsDelta,
        pointsLimit
      );
    }

    results.push({
      pointsAtEnd: rosterPointsAtEnd,
      pointsAtStart: rosterPointsAtStart,
      pointsDelta: rosterPointsDelta,
      rosterId,
    });

    runningScores[rosterId] = rosterPointsAtEnd;
  }

  return {
    ...base,
    results,
  };
};
